import { useEffect, type RefObject } from 'react';

/**
 * useClickOutside — fires the handler when a `mousedown` event occurs
 * outside the ref'd element.
 *
 * Extracted from the verbatim pattern in `frontend/src/components/layout/UserMenu.tsx`
 * lines 24-36 (PATTERNS.md). Reusable across BellDropdown (Wave 2 plan 41-03),
 * tier pickers, and any other dismissible dropdown surface.
 *
 * Caller passes a ref produced by `useRef<HTMLElement>()` (or any HTMLElement
 * subtype) and a stable handler. The handler fires only when the click target
 * is NOT contained by the ref'd element.
 */
export function useClickOutside(
  ref: RefObject<HTMLElement | null>,
  handler: () => void
): void {
  useEffect(() => {
    function listener(e: MouseEvent) {
      if (!ref.current || ref.current.contains(e.target as Node)) return;
      handler();
    }
    document.addEventListener('mousedown', listener);
    return () => document.removeEventListener('mousedown', listener);
  }, [ref, handler]);
}
